import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getDimIntroImages, getDimIntroImageCurrent, uploadDimIntroImage,
  activateDimIntroImage, deleteDimIntroImage, restoreDimIntroImageDefault,
} from '../../api/admin';
import AdminLayout from '../../components/AdminLayout';
import Button from '../../components/Button';
import Alert from '../../components/Alert';

// V4 F.2 / M24: 维度介绍图后台
// 后端: apps/dimensions/dim_intro_views.py
// - 上传 = POST 新版（image_pc 必传 / image_mobile 可选）→ 自动 bump version + 设 active + 关旧 active
// - 不支持 PATCH（替换走新版本）
// - DELETE 仅老版本可删
// - GET current/ 返回当前生效图 + dim_updated_at（用于"维度变更但图未同步"banner）

const MAX_BYTES = 2 * 1024 * 1024;  // 2MB, 与设计文档第 7.1 节"<2MB"对齐

export default function AdminDimIntroImagesPage() {
  const [list, setList] = useState([]);
  const [current, setCurrent] = useState(null);  // {image_pc, image_mobile, version, latest_dim_updated_at, uploaded_at}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 上传表单
  const [pcFile, setPcFile] = useState(null);
  const [mobileFile, setMobileFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [{ data: listResp }, { data: currentResp }] = await Promise.all([
        getDimIntroImages(),
        getDimIntroImageCurrent(),
      ]);
      setList(Array.isArray(listResp) ? listResp : (listResp.results ?? []));
      setCurrent(currentResp.current === null ? null : currentResp);
    } catch (e) {
      setError(e.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dimMismatchBanner = useMemo(() => {
    // 7.3: 维度配置 updated_at > 当前介绍图 uploaded_at → 提示同步更新
    if (!current?.uploaded_at || !current?.latest_dim_updated_at) return null;
    const dimAt = new Date(current.latest_dim_updated_at).getTime();
    const imgAt = new Date(current.uploaded_at).getTime();
    if (dimAt > imgAt) {
      return `⚠️ 维度配置在 ${formatDate(current.latest_dim_updated_at)} 有变更，但当前介绍图（v${current.version}）上传于 ${formatDate(current.uploaded_at)}。建议确认介绍图与最新维度匹配并上传新版。`;
    }
    return null;
  }, [current]);

  const validateFiles = () => {
    if (!pcFile) return '请选择 PC 端介绍图（必传）';
    if (!pcFile.type.startsWith('image/')) return 'PC 图必须是图片格式（PNG / JPG）';
    if (pcFile.size > MAX_BYTES) return `PC 图过大（${(pcFile.size / 1024 / 1024).toFixed(1)} MB），建议 < 2MB`;
    if (mobileFile) {
      if (!mobileFile.type.startsWith('image/')) return '移动端图必须是图片格式';
      if (mobileFile.size > MAX_BYTES) return `移动端图过大（${(mobileFile.size / 1024 / 1024).toFixed(1)} MB），建议 < 2MB`;
    }
    return null;
  };

  const handleUpload = async () => {
    const v = validateFiles();
    if (v) { setError(v); return; }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('image_pc', pcFile);
      if (mobileFile) fd.append('image_mobile', mobileFile);
      const { data } = await uploadDimIntroImage(fd);
      setSuccess(`已上传新版本 v${data.version}（自动激活）`);
      setPcFile(null);
      setMobileFile(null);
      // 清空 input 元素的选中状态
      document.getElementById('pc-input').value = '';
      const mb = document.getElementById('mb-input');
      if (mb) mb.value = '';
      load();
    } catch (e) {
      const data = e.response?.data || {};
      const firstField = Object.keys(data).find((k) => Array.isArray(data[k]) && data[k][0]);
      const msg = firstField
        ? `${firstField}: ${data[firstField][0]}`
        : (data.detail || '上传失败');
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleActivate = async (img) => {
    try {
      await activateDimIntroImage(img.id);
      setSuccess(`已激活 v${img.version}`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || '激活失败');
    }
  };

  const handleDelete = async (img) => {
    if (img.is_active) {
      setError('当前生效的版本不能删除：请先激活其它版本');
      return;
    }
    if (!window.confirm(`确认删除 v${img.version}？\n该版本将永久从图库移除（图片文件保留, 不引用即可被 GC）。`)) return;
    try {
      await deleteDimIntroImage(img.id);
      setSuccess(`已删除 v${img.version}`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || '删除失败');
    }
  };

  const handleRestoreDefault = async () => {
    // 行为：把所有 is_active 置 false → 首页 fallback 到默认 8 卡片网格
    // 注意提示用户：历史图不删, 后续 activate 任意版本就能换回来
    if (!window.confirm(
      '确认回滚到默认样式？\n\n首页"维度介绍"区域将切回默认的 8 个文字卡片网格。\n'
      + '历史版本图片不会删除, 之后想换回来直接「激活」任意一版即可。'
    )) return;
    try {
      const { data } = await restoreDimIntroImageDefault();
      setSuccess(
        data.deactivated > 0
          ? `已回滚到默认样式（关闭 ${data.deactivated} 张生效图）`
          : '当前已是默认样式, 无需回滚'
      );
      load();
    } catch (e) {
      setError(e.response?.data?.detail || '回滚失败');
    }
  };

  return (
    <AdminLayout title="维度介绍图">
      {error && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}
      {success && <Alert type="success" style={{ marginBottom: 12 }}>{success}</Alert>}
      {dimMismatchBanner && (
        <Alert type="warning" style={{ marginBottom: 12 }}>{dimMismatchBanner}</Alert>
      )}

      <div style={{ marginBottom: 16, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
        首页"维度介绍"区域读取此处的当前生效图（PC + 移动端各一张, 移动端可选）。
        上传新版本会<b>自动激活并替换</b>旧版（旧版保留在图库, 必要时可激活回滚）。
        建议尺寸 &lt; 2MB / PNG 或 JPG。
      </div>

      {/* 上传新版 */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
        padding: 24, marginBottom: 20,
      }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1a3a6b', marginBottom: 16 }}>
          上传新版本
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>
              PC 端介绍图 <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input id="pc-input" type="file" accept="image/png,image/jpeg"
              onChange={(e) => setPcFile(e.target.files?.[0] || null)} />
            {pcFile && (
              <div style={previewBoxStyle}>
                <img src={URL.createObjectURL(pcFile)} alt="PC 预览" style={previewImgStyle} />
                <span style={previewMetaStyle}>
                  {pcFile.name} · {(pcFile.size / 1024).toFixed(0)} KB
                </span>
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>
              移动端介绍图 <span style={{ color: '#94a3b8', fontWeight: 400 }}>（可选, 不传则前端 fallback PC 图）</span>
            </label>
            <input id="mb-input" type="file" accept="image/png,image/jpeg"
              onChange={(e) => setMobileFile(e.target.files?.[0] || null)} />
            {mobileFile && (
              <div style={previewBoxStyle}>
                <img src={URL.createObjectURL(mobileFile)} alt="移动 预览" style={previewImgStyle} />
                <span style={previewMetaStyle}>
                  {mobileFile.name} · {(mobileFile.size / 1024).toFixed(0)} KB
                </span>
              </div>
            )}
          </div>
        </div>
        <Button onClick={handleUpload} disabled={uploading || !pcFile}>
          {uploading ? '上传中...' : '上传并激活'}
        </Button>
      </div>

      {/* 当前生效卡片 */}
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#475569', marginBottom: 10 }}>当前生效</h3>
      {!current ? (
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px dashed #e2e8f0',
          padding: 32, marginBottom: 20, textAlign: 'center', color: '#94a3b8',
        }}>
          尚未上传任何版本。前端首页将 fallback 到 Dimension.description 文字版介绍。
        </div>
      ) : (
        <div style={{
          background: '#fff', borderRadius: 12, border: '2px solid #16a34a40',
          padding: 20, marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <span style={{
                display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                fontSize: 12, fontWeight: 700, background: '#dcfce7', color: '#16a34a',
              }}>v{current.version} · 生效中</span>
              <span style={{ marginLeft: 10, fontSize: 12, color: '#64748b' }}>
                上传于 {formatDate(current.uploaded_at)}
              </span>
            </div>
            {/* 回滚到默认样式: 把所有 is_active 置 false → 首页走默认 8 卡片网格 fallback。
                历史图本身不删, 之后想换回来 activate 任意一版即可。 */}
            <button
              onClick={handleRestoreDefault}
              style={btn('#475569')}
              title="把首页维度介绍区域切回默认 8 卡片网格（历史图保留）"
            >
              回滚到默认样式
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: current.image_mobile ? '2fr 1fr' : '1fr', gap: 16 }}>
            <div>
              <div style={imgLabelStyle}>PC 端</div>
              <img src={withVersion(current.image_pc, current.version)} alt="PC 当前生效" style={liveImgStyle} />
            </div>
            {current.image_mobile && (
              <div>
                <div style={imgLabelStyle}>移动端</div>
                <img src={withVersion(current.image_mobile, current.version)} alt="移动 当前生效" style={liveImgStyle} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* 历史版本图库 */}
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#475569', marginBottom: 10 }}>历史版本</h3>
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>加载中...</div>
        ) : list.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>暂无历史版本</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['版本', 'PC 缩略', '移动 缩略', '上传人', '上传时间', '状态', '操作'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((img, idx) => (
                <tr key={img.id} style={{
                  borderBottom: idx < list.length - 1 ? '1px solid #f1f5f9' : 'none',
                  opacity: img.is_active ? 1 : 0.85,
                  background: img.is_active ? '#f0fdf4' : 'transparent',
                }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>v{img.version}</td>
                  <td style={tdStyle}>
                    {img.image_pc ? <img src={img.image_pc} alt="" style={thumbStyle} /> : '—'}
                  </td>
                  <td style={tdStyle}>
                    {img.image_mobile ? <img src={img.image_mobile} alt="" style={thumbStyle} /> : '—'}
                  </td>
                  <td style={tdStyle}>{img.uploaded_by_name || '—'}</td>
                  <td style={{ ...tdStyle, color: '#64748b' }}>{formatDate(img.uploaded_at)}</td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                      fontSize: 12, fontWeight: 600,
                      background: img.is_active ? '#dcfce7' : '#f1f5f9',
                      color: img.is_active ? '#16a34a' : '#94a3b8',
                    }}>{img.is_active ? '生效中' : '历史'}</span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {!img.is_active && (
                        <button onClick={() => handleActivate(img)} style={btn('#16a34a')}>激活</button>
                      )}
                      <button onClick={() => handleDelete(img)} style={btn(img.is_active ? '#94a3b8' : '#dc2626')}
                        disabled={img.is_active} title={img.is_active ? '当前生效版本不可删除' : ''}>
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p style={{ marginTop: 12, fontSize: 12, color: '#94a3b8' }}>
        共 {list.length} 个版本。前端按 <code style={inlineCode}>?v={'{version}'}</code> 拼 URL，激活后 CDN 缓存自动失效。
      </p>
    </AdminLayout>
  );
}

function withVersion(url, version) {
  if (!url) return '';
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${version}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 };
const thStyle = {
  padding: '10px 14px', textAlign: 'left', fontWeight: 600,
  color: '#475569', borderBottom: '1px solid #e2e8f0',
};
const tdStyle = { padding: '12px 14px', verticalAlign: 'middle', color: '#334155' };
const btn = (color) => ({
  padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
  background: color, color: '#fff', fontSize: 12, fontWeight: 600,
});
const previewBoxStyle = {
  marginTop: 8, padding: 8, background: '#f8fafc', borderRadius: 8,
  border: '1px solid #e2e8f0',
  display: 'flex', alignItems: 'center', gap: 10,
};
const previewImgStyle = {
  height: 60, maxWidth: 120, objectFit: 'contain',
  borderRadius: 4, background: '#fff',
};
const previewMetaStyle = { fontSize: 11, color: '#64748b' };
const liveImgStyle = {
  width: '100%', maxHeight: 360, objectFit: 'contain',
  borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0',
};
const thumbStyle = {
  height: 40, maxWidth: 80, objectFit: 'contain',
  borderRadius: 4, background: '#f8fafc', border: '1px solid #e2e8f0',
};
const imgLabelStyle = { fontSize: 12, color: '#64748b', marginBottom: 6, fontWeight: 600 };
const inlineCode = {
  fontFamily: 'ui-monospace, monospace', fontSize: 11,
  background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, color: '#1a3a6b',
};
